import { Box, Chip, IconButton, TableCell, TableRow, Typography } from '@mui/material';
import { MouseEvent, useState } from 'react';
import zeplinColor from 'theme/Color';
import { Task } from '../../../../../../../model/tasks.model';
import { TasksSettings } from '../../../TasksSettings/TasksSettings';
import { formatDate, getPriorityColor, getPriorityLabel } from '../../utils';
import { TaskAssignee } from '../TaskAssignee/TaskAssignee';

type TaskRowProps = {
  task: Task;
  onTaskClick: (task: Task) => void;
  onEditTask: (task: Task) => void;
  onDeleteTask: (task: Task) => void;
  onStatusChange: () => void;
};

const getRowColor = (taskHealth?: string): { borderColor: string; bgColor: string } => {
  const healthLower = taskHealth ? String(taskHealth).toLowerCase() : '';
  
  if (healthLower === 'overdue') {
    return { borderColor: zeplinColor.Error, bgColor: zeplinColor.BlockBG };
  }
  
  if (healthLower === 'warning') {
    return { borderColor: zeplinColor.AlertInfo, bgColor: zeplinColor.AlertInfoLight };
  }
  
  return { borderColor: zeplinColor.Neutral5, bgColor: 'none' };
};

export const TaskRow = ({
  task,
  onTaskClick,
  onEditTask,
  onDeleteTask,
  onStatusChange,
}: TaskRowProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const priority = getPriorityLabel(task.priority);
  const priorityColor = getPriorityColor(priority);
  const { borderColor, bgColor } = getRowColor(task.taskHealth);

  return (
    <TableRow
      key={task.id}
      hover
      sx={{
        '&:last-child td, &:last-child th': { borderBottom: `2px solid ${borderColor}` },
        textDecoration: 'none',
        color: 'inherit',
        bgcolor: bgColor === 'none' ? 'transparent' : bgColor,
        borderLeft: `2px solid ${borderColor}`,
        cursor: 'pointer',
      }}
      onClick={() => onTaskClick(task)}
    >
      <TableCell 
        sx={{ 
          padding: '22px 12px',
          borderBottom: `1px solid ${borderColor}`,
          borderTop: `1px solid ${borderColor}`,
        }}
      >
        <Typography variant='body2' sx={{ fontWeight: 500 }} noWrap>
          {task.subject || 'No Subject'}
        </Typography>
      </TableCell>
      <TableCell 
        sx={{ 
          padding: '22px 12px',
          borderBottom: `1px solid ${borderColor}`,
          borderTop: `1px solid ${borderColor}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <TaskAssignee task={task} />
      </TableCell>
      <TableCell 
        sx={{ 
          padding: '22px 12px',
          borderBottom: `1px solid ${borderColor}`,
          borderTop: `1px solid ${borderColor}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {task.dueDate && (
            <>
              <Typography variant='body2'>{formatDate(task.dueDate)}</Typography>
              {task.isOverdue && (
                <img src='/assets/overdue-icon.svg' width={16} height={16} alt='Overdue' />
              )}
            </>
          )}
        </Box>
      </TableCell>
      <TableCell 
        sx={{ 
          padding: '22px 12px',
          borderBottom: `1px solid ${borderColor}`,
          borderTop: `1px solid ${borderColor}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Chip
            label={
              <Typography variant='label1' sx={{ fontWeight: 500, fontSize: '12px' }}>
                {priority}
              </Typography>
            }
            sx={{
              bgcolor: priorityColor,
              color: zeplinColor.Neutral1,
              height: '22px',
              padding: '4px 12px',
              borderRadius: '50px',
              '& .MuiChip-label': {
                padding: 0,
              },
            }}
          />
          <IconButton
            size='small'
            sx={{
              padding: '4px',
              '&:hover': {
                backgroundColor: zeplinColor.Neutral4,
              },
            }}
            onClick={(e: MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              setAnchorEl(e.currentTarget);
            }}
          >
            <img src='/assets/vertical-dots.svg' width={20} height={20} alt='Menu' />
          </IconButton>
          <TasksSettings
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            task={task}
            onViewDetails={() => onTaskClick(task)}
            onEdit={() => onEditTask(task)}
            onStatusChange={onStatusChange}
            onDelete={() => onDeleteTask(task)}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
};
